import { useEffect, useState } from "react";
import axiosInstance from "../helpers/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import './viewEmployee.css';
import Swal from "sweetalert2";
import Edit from '../images/edit.png';

const ViewEmployee = () => {
    const { id } = useParams();
    const [employee, setEmployee] = useState(null);
    const [locationHistory, setLocationHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedEmployee, setEditedEmployee] = useState({});
    const [locations, setLocations] = useState([]);
    const [emailError, setEmailError] = useState("");
    const [showAllHistory, setShowAllHistory] = useState(false);
    const visibleHistory = showAllHistory ? locationHistory : locationHistory.slice(0, 2);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchVendorDetails = async () => {
            try {
                const response = await axiosInstance.get(`/api/employee/employee/${id}`);
                setEmployee(response.data.employee);
            } catch (err) {
                setError("Failed to fetch employee details.");
                console.error("Error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchVendorDetails();
    }, [id]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axiosInstance.get('/api/plant/AllLocations');
                setLocations(response.data.data);
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        const fetchLocationHistory = async () => {
            if (employee?.id) {
                try {
                    const response = await axiosInstance.get(`/api/employee/fetchLocation/${employee.id}`);
                    setLocationHistory(response.data.history);
                } catch (error) {
                    console.error("Error fetching location history:", error);
                }
            }
        };

        fetchLocationHistory();
    }, [employee]);



    if (loading) return <p>Loading employee details...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    const handleSavePersonal = async () => {
        try {
            await axiosInstance.put(`/api/employee/employeeDetails/${employee.id}`, editedEmployee);
            const updatedLocation = locations.find(loc => loc.id === Number(editedEmployee.location_id));
            setEmployee((prev) => ({
                ...prev,
                ...editedEmployee,
                location: updatedLocation || prev.location,
            }));

            Swal.fire("Success", "Employee updated successfully", "success");
            setIsEditing(false);
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Failed to update employee", "error");
        }
    };


    const handleDelete = async (employeeId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you want to delete this employee?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/employee/employee/${employeeId}`);
                    Swal.fire({
                        title: "Deleted!",
                        text: "Employee has been deleted.",
                        icon: "success",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "OK",
                    }).then(() => {
                        navigate(-1);
                    });
                } catch (err) {
                    Swal.fire({
                        title: "Error!",
                        text: err.response?.data?.message || "Failed to delete employee.",
                        icon: "error",
                        confirmButtonColor: "#d33",
                        confirmButtonText: "OK",
                    });
                    console.error("Error:", err);
                }
            }
        });
    };


    const handleActivate = async (employeeId) => {
        Swal.fire({
            title: "Activate Employee?",
            text: "Do you want to activate this employee?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#198754",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, activate",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axiosInstance.put(`/api/employee/activate/${employeeId}`);
                    Swal.fire({
                        title: "Activated!",
                        text: "Employee has been activated.",
                        icon: "success",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "OK",
                    }).then(() => {
                        navigate(-1);
                    });
                } catch (err) {
                    Swal.fire({
                        title: "Error!",
                        text: err.response?.data?.message || "Failed to activate employee.",
                        icon: "error",
                        confirmButtonColor: "#d33",
                        confirmButtonText: "OK",
                    });
                    console.error("Error:", err);
                }
            }
        });
    }

    return (
        <div className="container py-4">
            <div className="responsive-scroll">
                <div className="d-flex justify-content-end mb-3 gap-3">
                    {employee && (
                        <>
                            {employee.status === "active" ? (
                                <button
                                    className="btn btn-sm"
                                    onClick={() => handleDelete(employee.id)}
                                    style={{ backgroundColor: "rgb(198, 77, 77)", color: "white" }}
                                >
                                    <i className="bi bi-trash me-1"></i>Delete
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm"
                                    onClick={() => handleActivate(employee.id)}
                                    style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                                >
                                    <i className="bi bi-check-circle me-1"></i>Activate
                                </button>
                            )}

                            <button className="back-button p-2" onClick={() => navigate(-1)}>
                                <i className="bi bi-arrow-left"></i>
                                Back to Employees List
                            </button>
                        </>
                    )}
                </div>


                <div className="row g-4 align-items-start">
                    {/* Left Section - Personal Info */}
                    <div className="col-12 col-md-4">
                        <div className="bg-white p-3 shadow-sm position-relative" style={{ height: "100%" }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center flex-wrap gap-2">
                                    <h6 className="card-title m-0 me-2" style={{ fontSize: "1.1rem" }}>👤 Personal Info</h6>
                                    <span className={`status-badge ${employee.status?.toLowerCase()}`} style={{ fontSize: "0.8rem" }}>
                                        {employee.status ? employee.status.toUpperCase() : "--"}
                                    </span>
                                </div>
                                <img
                                    src={Edit}
                                    alt="Edit"
                                    title="Edit"
                                    onClick={() => {
                                        setIsEditing(true);
                                        setEditedEmployee(employee);
                                    }}
                                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                />
                            </div>

                            {/* Name */}
                            <div className="info-item mb-2">
                                <label className="info-label text-muted" style={{ fontSize: "14px" }}>Name</label>
                                <div className="mt-0 d-flex align-items-center" style={{ fontSize: "17px" }}>
                                    <i className="bi bi-person-circle me-2 text-primary"></i>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={employee.name}
                                            value={editedEmployee.name || ""}
                                            onChange={(e) =>
                                                setEditedEmployee({ ...editedEmployee, name: e.target.value })
                                            }
                                        />
                                    ) : (
                                        <span>{employee.name || "--"}</span>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className="info-item mb-2">
                                <label className="info-label text-muted" style={{ fontSize: "14px" }}>Email</label>
                                <div className="mt-0 d-flex align-items-center" style={{ fontSize: "17px" }}>
                                    <i className="bi bi-envelope-fill me-2 text-primary"></i>
                                    {isEditing ? (
                                        <div className="flex-grow-1">
                                            <input
                                                type="email"
                                                className="form-control form-control-sm"
                                                placeholder="email@gmail.com"
                                                value={editedEmployee.email || ""}
                                                required
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEditedEmployee({ ...editedEmployee, email: value });

                                                    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                    if (!emailPattern.test(value) && value !== "") {
                                                        setEmailError("Invalid email format");
                                                    } else {
                                                        setEmailError("");
                                                    }
                                                }}
                                            />
                                            {emailError && (
                                                <small className="text-danger">{emailError}</small>
                                            )}
                                        </div>
                                    ) : (
                                        <span>{employee.email || "--"}</span>
                                    )}
                                </div>
                            </div>

                            {/* Mobile */}
                            <div className="info-item mb-2">
                                <label className="info-label text-muted" style={{ fontSize: "14px" }}>Mobile</label>
                                <div className="mt-0 d-flex align-items-center" style={{ fontSize: "17px" }}>
                                    <i className="bi bi-telephone-fill me-2 text-primary"></i>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            maxLength={10}
                                            placeholder={employee.mobile_number}
                                            value={editedEmployee.mobile_number || ""}
                                            onChange={(e) =>
                                                setEditedEmployee({
                                                    ...editedEmployee,
                                                    mobile_number: e.target.value,
                                                })
                                            }
                                        />
                                    ) : (
                                        <span>{employee.mobile_number || "--"}</span>
                                    )}
                                </div>
                            </div>

                            {/* Role */}
                            <div className="info-item mb-2">
                                <label className="info-label text-muted" style={{ fontSize: "14px" }}>Role</label>
                                <div className="mt-0 d-flex align-items-center" style={{ fontSize: "17px" }}>
                                    <i className="bi bi-person-badge-fill me-2 text-primary"></i>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            disabled
                                            placeholder={employee.role}
                                            value={editedEmployee.role || ""}
                                            onChange={(e) =>
                                                setEditedEmployee({ ...editedEmployee, role: e.target.value })
                                            }
                                        />
                                    ) : (
                                        <span>{employee.role || "--"}</span>
                                    )}
                                </div>
                            </div>

                            {/* Assigned Location */}
                            <div>
                                <label className="info-label text-muted" style={{ fontSize: "14px" }}>
                                    Assigned Location
                                </label>
                                <div className="mt-1 d-flex align-items-center" style={{ fontSize: "17px" }}>
                                    <i className="bi bi-geo-alt-fill me-2 text-danger"></i>
                                    {isEditing ? (
                                        employee?.role === "operator" ? (
                                            <select
                                                name="location"
                                                className="form-select form-select-sm"
                                                value={editedEmployee?.location_id || ""}
                                                onChange={(e) =>
                                                    setEditedEmployee({
                                                        ...editedEmployee,
                                                        location_id: e.target.value,
                                                    })
                                                }
                                            >
                                                <option value="">Select Location</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {loc.location_name}{" "}
                                                        {loc.module ? `${loc.module.module_name}` : "(N/A)"}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="form-control form-control-sm bg-light text-muted" disabled>
                                                {employee?.location?.location_name || "--"}
                                            </span>
                                        )
                                    ) : (
                                        <span>{employee?.location?.location_name || "--"}</span>
                                    )}
                                </div>
                            </div>


                            {/* Buttons */}
                            {isEditing && (
                                <div className="mt-2 d-flex gap-2 justify-content-end flex-wrap">
                                    <button
                                        className="btn btn-sm"
                                        onClick={handleSavePersonal}
                                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                                    >
                                        Save
                                    </button>
                                    <button className="btn btn-sm btn-secondary" onClick={() => setIsEditing(false)}>
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-12 col-md-8">
                        <div className="location-history-card shadow-sm border-0">
                            <div className="location-history-header d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-clock-history fs-5 text-primary"></i>
                                    <h5 className="mb-0" style={{ fontSize: "1.1rem" }}>Location Assignment History</h5>
                                </div>
                            </div>

                            <div className="location-history-body overflow-auto" style={{ maxHeight: "480px" }}>
                                {locationHistory.length > 0 ? (
                                    <div className="location-history-timeline">
                                        {[...visibleHistory]
                                            .sort((a, b) => {
                                                const aLatest = new Date(a.unassigned_at || a.assigned_at || 0);
                                                const bLatest = new Date(b.unassigned_at || b.assigned_at || 0);
                                                return bLatest - aLatest;
                                            })
                                            .map((history, index) => (
                                                <div key={index} className="d-flex align-items-start gap-3 mb-3 position-relative">
                                                    <div
                                                        className="rounded-circle position-absolute"
                                                        style={{
                                                            width: "12px",
                                                            height: "12px",
                                                            backgroundColor:
                                                                index === 0
                                                                    ? history.unassigned_at
                                                                        ? "#b91c1c"
                                                                        : "#15803d"
                                                                    : "#adb5bd",
                                                            left: "-23px",
                                                            top: "20px",
                                                        }}
                                                    ></div>

                                                    <div
                                                        className="bg-white border p-3 rounded w-100"
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <h6
                                                                className="mb-0 fw-bold"
                                                                style={{
                                                                    color:
                                                                        index === 0
                                                                            ? history.unassigned_at
                                                                                ? "#b91c1c"
                                                                                : "#15803d"
                                                                            : "#adb5bd"
                                                                }}
                                                            >
                                                                {history.unassigned_at ? "REMOVED" : "ASSIGNED"}
                                                            </h6>
                                                        </div>

                                                        {history.assigned_at && (
                                                            <p className="text-muted mb-1 small">
                                                                <i className="bi bi-calendar-event me-2"></i>
                                                                Assigned: {new Date(history.assigned_at).toLocaleString()}
                                                            </p>
                                                        )}
                                                        {history.unassigned_at && (
                                                            <p className="text-muted mb-1 small">
                                                                <i className="bi bi-calendar-x me-2"></i>
                                                                Unassigned: {new Date(history.unassigned_at).toLocaleString()}
                                                            </p>
                                                        )}
                                                        <p className="fw-medium mb-0 text-dark">
                                                            <i className="bi bi-geo-alt me-2"></i>
                                                            {history.location?.location_name || "N/A"}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        {locationHistory.length > 2 && (
                                            <div className="text-center mt-3">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => setShowAllHistory(!showAllHistory)}
                                                >
                                                    {showAllHistory ? "View Less" : "View More"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-muted">No location history available.</p>
                                )}
                            </div>
                        </div>
                    </div>


                </div>
            </div>


        </div>
    );
};

export default ViewEmployee;
